import type { AdminAddonGroup, LocalizedPair } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { Plus, Tag } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { BilingualField } from '@/components/bilingual';
import { FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { PublishBlockers, RowActions, StatusBadge, TranslationBadge } from '@/components/records';
import { DataTable, EmptyState, Pagination, SkeletonRows, Toolbar } from '@/components/ui';
import {
  apiPatch,
  apiPost,
  useAdminList,
  useAdminMutation,
  useLifecycle,
  useListParams,
} from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { ListFilters } from './_filters';

export const GROUPS_URL = '/admin/addon-groups';
/** The list this screen's form pages return to. */
export const ADDONS_LIST = '/menu/addons';
const INVALIDATE = [['addon-groups'], ['addon-group'], qk.dashboard];

export function AddonsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const [params, setParams] = useListParams();
  const list = useAdminList<AdminAddonGroup>(qk.addonGroups(params), GROUPS_URL, params);
  const lifecycle = useLifecycle(GROUPS_URL, INVALIDATE);

  return (
    <>
      <PageHeader
        title={t('addons.title')}
        description={t('addons.subtitle')}
        actions={
          canEdit && (
            <Link to={`${ADDONS_LIST}/new`} className="dio-btn dio-btn--primary">
              <Plus className="dio-icon" aria-hidden="true" />
              {t('addons.newGroup')}
            </Link>
          )
        }
      />

      <Toolbar>
        <ListFilters params={params} onChange={setParams} />
      </Toolbar>

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState icon={<Tag aria-hidden="true" />} title={t('addons.empty')} hint={t('addons.subtitle')} />
      ) : (
        <DataTable
          caption={t('addons.title')}
          columns={[
            t('addons.groupName'),
            t('addons.optionCount'),
            t('addons.usageCount'),
            t('common.status'),
            <span className="sr-only">{t('common.actions')}</span>,
          ]}
        >
          {list.rows.map((row) => (
            <tr key={row.id}>
              <td>
                <div className="dio-table__name flex flex-wrap items-center gap-2">
                  <span lang="ar" dir="rtl">
                    {row.name.ar || '—'}
                  </span>
                  <span className="dio-meta" aria-hidden="true">
                    /
                  </span>
                  <span lang="en" dir="ltr">
                    {row.name.en || '—'}
                  </span>
                </div>
                <TranslationBadge translation={row.translation} />
              </td>

              <td className="dio-table__num">{row.optionCount}</td>

              <td className="dio-table__sub">{t('addons.usageItems', { count: row.usageCount })}</td>

              <td>
                <StatusBadge status={row.publicationStatus} />
              </td>

              <td className="dio-table__actions">
                <div className="dio-actions">
                  {/* The options list is this row's contents rather than an
                      action on it, and it is where the work actually happens. */}
                  <Link
                    to={`${ADDONS_LIST}/${row.id}/options`}
                    className="dio-btn dio-btn--secondary dio-btn--sm"
                  >
                    {t('addons.options')}
                  </Link>
                  <Link to={`${ADDONS_LIST}/${row.id}`} className="dio-btn dio-btn--secondary dio-btn--sm">
                    {canEdit ? t('common.edit') : t('common.details')}
                  </Link>
                  <RowActions
                    status={row.publicationStatus}
                    translation={row.translation}
                    canEdit={canEdit}
                    busy={lifecycle.busy}
                    label={t('common.openMenuFor', { name: row.name.ar || row.name.en })}
                    archiveWarning={
                      row.usageCount > 0 ? t('addons.archiveWarning', { count: row.usageCount }) : undefined
                    }
                    handlers={{
                      onPublish: () => lifecycle.publish.mutate({ id: row.id, version: row.version }),
                      onUnpublish: () => lifecycle.unpublish.mutate({ id: row.id, version: row.version }),
                      onArchive: () =>
                        lifecycle.archive.mutate({
                          id: row.id,
                          version: row.version,
                          confirmDependents: true,
                        }),
                      onRestore: () => lifecycle.restore.mutate({ id: row.id, version: row.version }),
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <Pagination
        page={params.page ?? 1}
        pageSize={params.pageSize ?? 20}
        total={list.total}
        onPage={(page) => setParams({ page })}
      />

    </>
  );
}

export function AddonGroupFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const record = useRecord<AdminAddonGroup>(qk.addonGroup(id ?? ''), GROUPS_URL, id);

  if (!id) return <AddonGroupForm group={null} canEdit={canEdit} />;

  return (
    <RecordPage title={t('addons.editGroup')} backTo={ADDONS_LIST} record={record}>
      {(row) => <AddonGroupForm key={row.id} group={row} canEdit={canEdit} />}
    </RecordPage>
  );
}

function AddonGroupForm({ group, canEdit }: { group: AdminAddonGroup | null; canEdit: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState<LocalizedPair>(group?.name ?? { ar: '', en: '' });
  const [description, setDescription] = useState<LocalizedPair>(editablePair(group?.description));

  const dirty = useDirty({ name, description });

  const save = useAdminMutation(
    () => {
      const body = {
        nameAr: name.ar,
        nameEn: name.en,
        descriptionAr: description.ar,
        descriptionEn: description.en,
      };
      return group
        ? apiPatch(`${GROUPS_URL}/${group.id}`, { ...body, expectedVersion: group.version })
        : apiPost(GROUPS_URL, body);
    },
    {
      successMessage: group ? t('common.saved') : t('common.created'),
      invalidate: INVALIDATE,
      onSuccess: () => navigate(ADDONS_LIST),
    },
  );

  return (
    <FormPage
      title={group ? t('addons.editGroup') : t('addons.newGroup')}
      description={t('addons.subtitle')}
      backTo={ADDONS_LIST}
      dirty={dirty}
      saving={save.isPending}
      canSave={name.ar.trim().length > 0}
      onSave={canEdit ? () => save.mutate(undefined) : undefined}
      footerStart={
        group && (
          /*
           * Secondary, not ghost, and it carries the count.
           *
           * A ghost button has no border and no fill, so this read as plain
           * text at the foot of the form — and since a group has no price of
           * its own, the screen looked like one that had simply lost its price
           * field. It was reported as exactly that. The prices are one click
           * away; the click just had to look like one.
           */
          <Link
            to={`${ADDONS_LIST}/${group.id}/options`}
            className="dio-btn dio-btn--secondary"
            style={{ marginInlineEnd: 'auto' }}
          >
            {t('addons.optionsWithCount', { count: group.optionCount })}
          </Link>
        )
      }
    >
      {group && <PublishBlockers translation={group.translation} />}
      <BilingualField
        label={t('addons.groupName')}
        value={name}
        onChange={setName}
        required
        maxLength={100}
        disabled={!canEdit}
      />
      <BilingualField
        label={t('addons.groupDescription')}
        value={description}
        onChange={setDescription}
        multiline
        maxLength={500}
        disabled={!canEdit}
      />
      {/* Says where the price is, rather than leaving its absence to be read as
          an omission. A group is a container — "Extra", "Size" — and every
          price in the add-on model hangs off an option. */}
      <p className="dio-hint">{t('addons.priceLivesOnOptions')}</p>
      {group && group.publicationStatus === 'PUBLISHED' && (
        <p className="dio-hint">{t('addons.unpublishNote')}</p>
      )}
    </FormPage>
  );
}
